import { useQuery } from "@tanstack/react-query";
import {
  useEffect,
  useEffectEvent,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import * as v from "valibot";
import { PushSubscriptionSchema } from "../../shared/schemas";
import { serverExplained } from "../data/api";
import { useSubscribePush, useUnsubscribePush } from "../data/mutations";
import { pushOptions } from "../data/queries";
import { useLocale } from "../i18n";
import { browserSubscription, pushSupported, subscribeBrowser } from "./push";

const renewalFailed = "تعذر تحديث الإشعارات. افتح رنّة وحاول مجددًا.";
const enableFailed =
  "تعذر تفعيل الإشعارات. تحقق من اتصالك وإعدادات الجهاز ثم حاول مجددًا.";
const disableFailed = "تعذر إيقاف الإشعارات. حاول مجددًا.";
const failure = (error: unknown, fallback: string) =>
  serverExplained(error) ? error.message : fallback;

export function useCallNotifications(accountId: string | undefined) {
  const locale = useLocale();
  const push = useQuery({
    ...pushOptions(accountId ?? null),
    enabled: !!accountId && pushSupported,
  });
  const subscribe = useSubscribePush(),
    unsubscribe = useUnsubscribePush();
  const [permission, setPermission] = useState<NotificationPermission>(() =>
    pushSupported ? Notification.permission : "default",
  );
  const [endpoint, setEndpoint] = useState<string | null>(null);
  const [checking, setChecking] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  const working = useRef(false);
  const identity = useRef(accountId);
  useLayoutEffect(() => {
    identity.current = accountId;
  }, [accountId]);
  const retryAction = useRef<"enable" | "disable" | "sync">("sync");
  const publicKey = push.data?.publicKey;
  const savedEndpoint = push.data?.subscription?.endpoint;
  const savedLang = push.data?.subscription?.lang;
  const save = useEffectEvent(async (subscription: PushSubscription) =>
    subscribe.mutateAsync({
      subscription: v.parse(PushSubscriptionSchema, subscription.toJSON()),
      lang: locale,
    }),
  );
  const remove = useEffectEvent(() => unsubscribe.mutateAsync());

  useEffect(() => {
    const refresh = () => {
      if (!document.hidden) setRevision((value) => value + 1);
    };
    const message = (event: MessageEvent) => {
      if (event.data?.type === "push-subscription-changed") refresh();
      if (event.data?.type === "push-renewal-failed") {
        retryAction.current = "sync";
        setError(renewalFailed);
      }
    };
    window.addEventListener("focus", refresh);
    window.addEventListener("online", refresh);
    document.addEventListener("visibilitychange", refresh);
    navigator.serviceWorker?.addEventListener("message", message);
    return () => {
      window.removeEventListener("focus", refresh);
      window.removeEventListener("online", refresh);
      document.removeEventListener("visibilitychange", refresh);
      navigator.serviceWorker?.removeEventListener("message", message);
    };
  }, []);

  useEffect(() => {
    if (!accountId || !publicKey || !pushSupported || working.current) return;
    let cancelled = false;
    setChecking(true);
    setError("");
    void (async () => {
      try {
        const permission = Notification.permission;
        setPermission(permission);
        const current = await browserSubscription();
        if (cancelled) return;
        setEndpoint(current?.endpoint ?? null);
        if (!navigator.onLine) throw new Error("Offline");
        if (permission !== "granted") {
          // A revoked permission must stop advertising this device as reachable.
          if (savedEndpoint) await remove();
          return;
        }
        // Never turn notifications on without an earlier subscription/opt-in.
        if (!current && !savedEndpoint) return;
        const subscription = await subscribeBrowser(publicKey);
        if (cancelled || working.current) return;
        setEndpoint(subscription.endpoint);
        if (subscription.endpoint !== savedEndpoint || savedLang !== locale)
          await save(subscription);
      } catch (error) {
        if (!cancelled) {
          retryAction.current = "sync";
          setError(failure(error, renewalFailed));
        }
      } finally {
        if (!cancelled) setChecking(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accountId, publicKey, savedEndpoint, savedLang, locale, revision]);

  const on =
    permission === "granted" && !!endpoint && endpoint === savedEndpoint;
  const change = async (enable: boolean) => {
    if (!publicKey || !accountId || working.current) return;
    const owner = accountId;
    working.current = true;
    setBusy(true);
    setError("");
    retryAction.current = enable ? "enable" : "disable";
    try {
      if (enable) {
        // Invoke synchronously from the tap, before registration or network I/O.
        const result = await Notification.requestPermission();
        if (identity.current !== owner) return;
        setPermission(result);
        if (result !== "granted") {
          if (result === "default")
            setError("لم تسمح بالإشعارات. اضغط تفعيل عندما تكون مستعدًا.");
          return;
        }
        const subscription = await subscribeBrowser(publicKey);
        if (identity.current !== owner) return;
        setEndpoint(subscription.endpoint);
        await subscribe.mutateAsync({
          subscription: v.parse(PushSubscriptionSchema, subscription.toJSON()),
          lang: locale,
        });
      } else {
        const subscription = await browserSubscription();
        if (subscription && !(await subscription.unsubscribe()))
          throw new Error("Unsubscribe failed");
        if (identity.current !== owner) return;
        setEndpoint(null);
        await unsubscribe.mutateAsync();
      }
    } catch (error) {
      if (identity.current === owner)
        setError(failure(error, enable ? enableFailed : disableFailed));
    } finally {
      working.current = false;
      setBusy(false);
      setChecking(false);
    }
  };
  const retry = () => {
    if (retryAction.current !== "sync")
      void change(retryAction.current === "enable");
    else {
      setRevision((value) => value + 1);
      void push.refetch();
    }
  };
  return {
    saved: push.data,
    savedFailed: push.isError,
    reloadSaved: () => void push.refetch(),
    on,
    permission,
    checking: !!publicKey && checking,
    busy,
    error,
    retry,
    change,
  };
}
export type CallNotifications = ReturnType<typeof useCallNotifications>;
